import React, { forwardRef, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import { getColumns, getDateAndDateTime, getRowData } from '../util'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ACTIVITY_TYPES, STANDARD_COLORS } from '../../../utility/constants'
import { fileServices } from '../../../services/fileServices'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { GetApp as GetAppIcon, Visibility } from '@mui/icons-material'
import LongMenu from '../../../pages/DataEntryPage/Fields/LongMenu'
import StandaloneFilePreview from '../../Dashboard/PreviewComponent/components/StandaloneFilePreview'
import { FIELD_TYPES } from '../../../utility/constants'
import { extractDisplayName } from '../../../utility/fileUtil'
import FieldHistory from '../../DataEntry/FieldHistory'

const downloadFile = async (user, value) => {
  await fileServices.downloadFile(user, value)
}

const getCellColor = (data, activityType) => {
  if (!data) return null

  if (activityType === ACTIVITY_TYPES.REVIEW) {
    if (data.review === undefined || data.review === null) return null
    return data.review === true
      ? STANDARD_COLORS.PREVIEW_ACCEPTED
      : STANDARD_COLORS.PREVIEW_REJECTED
  }

  if (activityType === ACTIVITY_TYPES.LONG_RUNNING) {
    if (data.review === undefined || data.review === null) return null
    return data.review === true
      ? STANDARD_COLORS.PREVIEW_ACCEPTED
      : STANDARD_COLORS.PREVIEW_REJECTED
  }

  return null
}

const renderValue = (data, { user, showPreview, color }) => {
  if (!data) return null

  switch (data.data_structure) {
    case FIELD_TYPES.FILE:
      return (
        <>
          <span style={{ color }}>{extractDisplayName(data.value)}</span>
          {data.value?.length > 0 && (
            <LongMenu
              options={[
                {
                  label: 'Preview',
                  icon: (
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  ),
                  onClick: () => showPreview(data.value),
                },
                {
                  label: 'Download',
                  icon: (
                    <IconButton size="small">
                      <GetAppIcon />
                    </IconButton>
                  ),
                  onClick: () => downloadFile(user, data.value),
                },
              ]}
            />
          )}
        </>
      )

    case FIELD_TYPES.DATE:
      return <span style={{ color }}>{getDateAndDateTime(data.value, data.data_structure)}</span>

    case FIELD_TYPES.DATETIME:
      return <span style={{ color }}>{getDateAndDateTime(data.value, data.data_structure)}</span>

    default:
      return <span style={{ color }}>{data.value}</span>
  }
}

function fixedHeaderContent(columns, headerHeight, headerRef) {
  return (
    <TableRow ref={headerRef}>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          variant="head"
          style={{
            width: column.width,
            position: column.isFrozen ? 'sticky' : 'relative',
            zIndex: column.isFrozen ? 300 : 200,
            top: 0,
            backgroundColor: 'white',
            left: column.isFrozen ? `${column.leftOffset}px` : 'auto',
            height: `${headerHeight}px`,
          }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  )
}

function rowContent(
  index,
  row,
  columns,
  activityType,
  user,
  showPreview,
  headerHeight,
  subfields,
  values,
  listData,
  dynamicListData,
  externalValues,
  fieldMappings,
  tableData,
  processId,
  listDataIdMap,
  listIdsWithPlaceholders
) {
    if (!row.is_header) {
    return (
      <>
        {columns.map((column) => {
          let data = row[column.dataKey]

          if (column.dataKey === 'slNo') {
            data = { value: index + 1 }
          }

          const isFrozen = column.isFrozen
          const leftOffset = column.leftOffset

          const color = getCellColor(data, activityType)

         const subFieldId = column.dataKey.replace('f_', '')
          const subField = subfields?.find((sf) => String(sf.id) === String(subFieldId))

          return (
            <TableCell
              key={column.dataKey}
              align={column.numeric ? 'right' : 'left'}
              style={{
                padding: 4,
                position: isFrozen ? 'sticky' : 'relative',
                left: isFrozen ? `${leftOffset}px` : 'auto',
                backgroundColor: 'white',
                width: column.width,
                zIndex: isFrozen ? 150 : 1,
                height: '100%',
                top: isFrozen ? `${headerHeight}px` : 'auto',
              }}
            >
              <div
                className="preview-timestramp"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'wrap',
                  wordBreak: 'break-all',
                  width: '100%',
                }}
              >
{subField ? (
                  <FieldHistory
                    fieldId={`${subField.id}__${row.rowNumber}`}
                    rowId={row.rowNumber}
                    field={subField}
                    listDataIdMap={listDataIdMap}
                    listIdsWithPlaceholders={listIdsWithPlaceholders}
                    dynamicListData={dynamicListData}
                    processId={processId}
                    allValues={values}
                    isPreview={true}
                    rowSuffix={`__${row.rowNumber}`}
                    externalValues={externalValues}
                    fieldMappings={fieldMappings}
                  >
                    {renderValue(data, { user, showPreview, color })}
                  </FieldHistory>
                ) : (
                  renderValue(data, { user, showPreview, color })
                )}              </div>
            </TableCell>
          )
        })}
      </>
    )
  } else {
    return (
      <TableCell
        style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0', padding: 4 }}
        colSpan={columns.length}
      >
        <Box minHeight={'27px'} display={'flex'} alignItems={'center'}>
          <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
            {row.value}
          </Typography>
        </Box>
      </TableCell>
    )
  }
}

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer className="responsiveTable" component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />,
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
}

const VirtualizedTable = ({
  activityType,
  rows,
  tableSequenceAndHeaders = [],
  subfields,
  values,
  reviews,
  hiddenColumns,
  listData,
  dynamicListData,
  previewCurrency,
  freezeColumnId,
  data,
  processId,
  externalValues = {},
  fieldMappings = {},
  fieldMetadata = {},
}) => {
  const { user } = useAuthContext()
  const [columns, setColumns] = useState([])
  const [filePreview, setFilePreview] = useState(null)

  // Build O(1) lookup maps once from listData
  const listDataIdMap = useMemo(() =>
    (listData || []).reduce((acc, it) => { acc[it.id] = it; return acc }, {})
  , [listData])

  const listIdsWithPlaceholders = useMemo(() => {
    const result = new Set()
    const seen = new Set()
    for (const it of (listData || [])) {
      if (it.list_id == null || seen.has(it.list_id)) continue
      seen.add(it.list_id)
      if ((Array.isArray(it.placeholders) && it.placeholders.length > 0) ||
          (it.value || '').includes('[[')) {
        result.add(it.list_id)
      }
    }
    return result
  }, [listData])

  const headerRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  // Memoize the full row-data transform so it only reruns when the underlying
  // data changes — not on every parent render (which was the key perf issue
  // causing N row × N-column work on every keystroke).
  const rowData = useMemo(
    () =>
      getRowData(
        rows,
        tableSequenceAndHeaders,
        values,
        reviews,
        subfields,
        hiddenColumns,
        listData,
        dynamicListData,
        previewCurrency,
        freezeColumnId,
        externalValues,
        fieldMappings,
        listDataIdMap,
        fieldMetadata,
        data,
      ),
    [
      rows,
      tableSequenceAndHeaders,
      values,
      reviews,
      subfields,
      hiddenColumns,
      listData,
      dynamicListData,
      previewCurrency,
      freezeColumnId,
      externalValues,
      fieldMappings,
      listDataIdMap,
      fieldMetadata,
      data,
    ],
  )

  useEffect(() => {
    if (headerRef.current) {
      const measuredHeight = headerRef.current.getBoundingClientRect().height
      setHeaderHeight(measuredHeight)
    }
  }, [columns])

  const subfieldsKey = useMemo(() => {
    return subfields?.map((sf) => sf.id).join(',')
  }, [subfields])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const updatedColumns = getColumns(subfields, hiddenColumns, freezeColumnId)
    setColumns(updatedColumns)
  }, [subfieldsKey, hiddenColumns, freezeColumnId])

  const renderFixedHeaderContent = useCallback(() => {
    return fixedHeaderContent(columns, headerHeight, headerRef)
  }, [columns, headerHeight])

  const renderItemContent = useCallback((_index, row) => {
    return rowContent(
      _index,
      row,
      columns,
      activityType,
      user,
      (file) => setFilePreview(file),
      headerHeight,
      subfields,
      values,
      listData,
      dynamicListData,
      externalValues,
      fieldMappings,
      data,
      processId,
      listDataIdMap,
      listIdsWithPlaceholders,
    )
  }, [
    columns,
    activityType,
    user,
    headerHeight,
    subfields,
    values,
    listData,
    dynamicListData,
    externalValues,
    fieldMappings,
    data,
    processId,
    listDataIdMap,
    listIdsWithPlaceholders,
  ])

  const computeItemKey = useCallback((_index, item) => {
    if (item.is_header) {
      return `header-${_index}-${item.value}`
    }
    return `row-${item.rowNumber}`
  }, [])

  return (
    <>
      <Paper style={{ height: 240, width: '100%' }}>
        <TableVirtuoso
          data={rowData}
          components={VirtuosoTableComponents}
          computeItemKey={computeItemKey}
          fixedHeaderContent={renderFixedHeaderContent}
          itemContent={renderItemContent}
        />
      </Paper>

      <StandaloneFilePreview
        fileKey={filePreview}
        open={filePreview != null}
        onClose={() => setFilePreview(null)}
      />
    </>
  )
}

export default React.memo(VirtualizedTable)
